import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import { FORM_STUDIO_COPY } from '../constants/formStudioConfig';
import { SETUP_STEP_KEYS, isSetupConnectorComplete } from '../utils/formSetupProgressUtils';

const STEP_LABEL_KEYS = {
  overview: 'overview',
  fields: 'fields',
  automations: 'automations',
};

function StepIndicator({ label, state, connectorComplete, isLast }) {
  const { semantic, radiusPx, typography } = useFeatureTheme();
  const palette = {
    complete: {
      dot: semantic.states.success,
      text: semantic.text.primary,
      line: semantic.states.success,
    },
    current: {
      dot: semantic.interactive.primary,
      text: semantic.interactive.primary,
      line: semantic.borders.border,
    },
    pending: {
      dot: semantic.surfaces.surface,
      text: semantic.text.secondary,
      line: semantic.borders.border,
      border: semantic.borders.border,
    },
    upcoming: {
      dot: semantic.surfaces.surfaceMuted,
      text: semantic.text.muted,
      line: semantic.borders.border,
      border: semantic.borders.borderSubtle ?? semantic.borders.border,
    },
    optional: {
      dot: semantic.surfaces.surfaceMuted,
      text: semantic.text.muted,
      line: semantic.borders.border,
      border: semantic.borders.borderSubtle ?? semantic.borders.border,
    },
  }[state] || palette.upcoming;

  return (
    <Stack direction="row" alignItems="center" sx={{ flex: isLast ? '0 0 auto' : 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: radiusPx.full,
            bgcolor: palette.dot,
            border: palette.border ? `2px solid ${palette.border}` : 'none',
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontSize: typography.fontSizeXs,
            fontWeight: state === 'current' ? typography.fontWeightSemibold : typography.fontWeightMedium,
            color: palette.text,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Stack>
      {!isLast ? (
        <Box
          sx={{
            flex: 1,
            height: 2,
            mx: 1,
            borderRadius: radiusPx.full,
            bgcolor: connectorComplete ? semantic.states.success : semantic.borders.border,
            minWidth: 12,
          }}
        />
      ) : null}
    </Stack>
  );
}

export default function FormSetupProgress({ setupProgress }) {
  const { semantic, radiusPx, typographyStyles } = useFeatureTheme();
  const copy = FORM_STUDIO_COPY.setupProgress;
  const stepStates = setupProgress?.stepStates || [];

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: radiusPx.md,
        border: `1px solid ${semantic.borders.border}`,
        bgcolor: semantic.surfaces.surfaceSubtle,
      }}
    >
      <Typography
        sx={{
          ...typographyStyles.caption,
          color: semantic.text.secondary,
          fontWeight: 600,
          mb: 1,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {copy.label}
      </Typography>
      <Stack direction="row" alignItems="center" sx={{ overflowX: 'auto'}}>
        {SETUP_STEP_KEYS.map((key, index) => (
          <StepIndicator
            key={key}
            label={copy[STEP_LABEL_KEYS[key]]}
            state={stepStates[index] || 'upcoming'}
            connectorComplete={isSetupConnectorComplete(stepStates, index)}
            isLast={index === SETUP_STEP_KEYS.length - 1}
          />
        ))}
      </Stack>
    </Box>
  );
}
